/**
 * 방문 분석(GA4·네이버 애널리틱스) 공통 설정과 GA4 이벤트 전송.
 *
 * 집계는 운영 도메인에서만 켠다 — 개발·미리보기(*.vercel.app)·로컬이 운영 통계를 오염시키지 않게.
 * 운전연수 사이트(web.dztraining)와 같은 규칙이다(ID·도메인만 다르다).
 */

/** GA4 측정 ID (웹 스트림: 면허취득 www.drivingzone.co.kr) */
export const GA_MEASUREMENT_ID = 'G-2FM6G96HT9';

/** 운영 루트 도메인. 지점 서브도메인(gangnam.drivingzone.co.kr 등)도 집계한다 */
export const ANALYTICS_ROOT_DOMAIN = 'drivingzone.co.kr';

/**
 * 전화·카카오 상담 링크가 눌린 화면 위치. GA4 에서 맞춤 측정기준 `placement` 로 본다.
 *
 * 같은 대표번호가 헤더·플로팅·바텀시트 여러 곳에 걸려 있어 이 값이 없으면 어느 화면이
 * 전화를 만들었는지 구분할 수 없다. 이미 쌓인 데이터와 이어지려면 값은 바꾸지 말고 추가만 한다.
 */
export const ContactPlacement = {
  /** 전국 시험장 안내 하단 바텀시트 */
  PROMO_BOTTOM_SHEET: 'promo_bottom_sheet',
  /** 모바일 우하단 플로팅 버튼 */
  MOBILE_FLOATING: 'mobile_floating',
  /** PC 우측 고정 CTA */
  SIDE_CTA: 'side_cta',
  /** 지점 상세 */
  BRANCH_DETAIL: 'branch_detail',
  /** 지점 캠페인 랜딩 */
  CAMPAIGN: 'campaign',
  /** 2주완성 요금제 상세 하단 고정 배너 */
  UNLIMITED_2WEEKS_STICKY: 'unlimited_2weeks_sticky',
  /** 위치 표시를 붙이지 않은 링크 */
  OTHER: 'other',
} as const;

export type ContactPlacementValue =
  (typeof ContactPlacement)[keyof typeof ContactPlacement];

/**
 * 링크(또는 그 조상)에 붙이는 위치 표시 속성.
 * 전화·카카오 링크는 화면 곳곳에 흩어져 있어 클릭을 document 한곳에서 받는다
 * (google-analytics.tsx). 그때 이 속성을 거슬러 올라가 위치를 알아낸다.
 */
export const PLACEMENT_ATTRIBUTE = 'data-ga-placement';

declare global {
  interface Window {
    /** gtag.js 전역(layout 의 구글 태그 스크립트가 만든다). 스크립트 실행 전에는 없다 */
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/** 운영 도메인인지. 브라우저에서만 판단한다(서버는 false) */
export function isAnalyticsHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return (
    host === ANALYTICS_ROOT_DOMAIN || host.endsWith(`.${ANALYTICS_ROOT_DOMAIN}`)
  );
}

/**
 * GA4 이벤트. 운영 도메인에서만, GA4 스트림으로만(send_to) 보낸다 — 같은 구글 태그에 걸린 광고 전환
 * 태그(AW-)로 새지 않게. 분석용이라 실패해도 예외를 던지지 않는다.
 * 매개변수에 이름·연락처 같은 개인정보를 넣지 않는다(GA 정책 위반).
 */
export function trackGaEvent(
  name: string,
  params: Record<string, string | number | undefined> = {},
): void {
  try {
    if (!isAnalyticsHost()) return;
    window.gtag?.('event', name, { ...params, send_to: GA_MEASUREMENT_ID });
  } catch {
    // 분석용 부가 기능 — 실패해도 화면 동작에 영향이 없어야 한다
  }
}
